import { useEffect, useState } from 'react';
import Input from '../Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import {
  emailScheme,
  passwordConfirmScheme,
  passwordScheme,
  stringScheme,
} from 'src/utils/validation-schemes';
import {
  ProjectsMultiSelect,
  type ProjectOption,
} from 'src/components/ProjectsMultiSelect/ProjectsMultiSelect';
import { useGetAllProjectsQuery } from 'src/services';
import { ROLES, ROLES_PRETTY } from 'src/constants/roles';
import { ProductsOrder } from 'src/constants/products';
import { useGetUsersMeQuery } from 'src/services/usersService';
import type { User } from 'src/services/usersService';
import type { Project } from 'src/services/projectsService';
import { UserNameFields, UserPasswordFields } from 'src/components/UserFormFields/UserFormFields';

// The changed fields (role as its value) plus the selected projects, which are always sent.
export type EditUserSubmitData = { selectedProjects: ProjectOption[]; [key: string]: unknown };

interface EditUserFormValues {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password2: string;
  role: { label: string; value: string };
}

export const EditUserForm = ({
  user,
  onSubmit,
}: {
  user: User;
  onSubmit: (data: EditUserSubmitData) => void;
}) => {
  const [roles, setRoles] = useState([
    { label: ROLES_PRETTY.admin, value: ROLES.ADMIN },
    { label: ROLES_PRETTY.manager, value: ROLES.MANAGER },
    { label: ROLES_PRETTY.support_team_lead, value: ROLES.SUPPORT_TEAM_LEAD },
    { label: ROLES_PRETTY.support, value: ROLES.SUPPORT },
  ]);
  const [sortedProjects, setSortedProjects] = useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<ProjectOption[]>([]);
  const { data: userInfo } = useGetUsersMeQuery();
  const {
    handleSubmit,
    control,
    trigger,
    formState: { dirtyFields },
  } = useForm<EditUserFormValues>({
    defaultValues: {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      password2: '',
      role: roles.find((role) => role.value === user.role),
    },
    resolver: formResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
        first_name: stringScheme({ required: true }).label('First name'),
        last_name: stringScheme({ required: false }).label('Last name'),
        password: passwordScheme({
          required: false,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        password2: passwordConfirmScheme({
          required: false,
          min: 8,
          minMessage: 'Min. 8 characters',
        }).label('Confirm password'),
      }),
    ),
  });

  const { data: projects = [] } = useGetAllProjectsQuery();

  useEffect(() => {
    if (projects?.length) {
      const clone = [...projects];

      clone.sort((a, b) => {
        return ProductsOrder[a.project_type] - ProductsOrder[b.project_type];
      });

      setSortedProjects(clone);

      const selected = clone.filter((project) =>
        user.projects?.find((userProject) => userProject.id === project.id),
      );

      setSelectedProjects(selected);
    }
  }, [projects]);

  useEffect(() => {
    if (userInfo?.role !== ROLES.COSSUPORT_MANAGER) {
      setRoles(roles.filter((role) => role.value !== ROLES.COSSUPORT_MANAGER));
    }
  }, [userInfo]);

  const submit = (formValues: EditUserFormValues) => {
    const filteredData = (
      Object.keys(dirtyFields) as (keyof EditUserFormValues)[]
    ).reduce<EditUserSubmitData>(
      (acc, key) => {
        if (dirtyFields[key]) {
          acc[key] = formValues[key];
        }
        return acc;
      },
      {
        selectedProjects,
      },
    );

    // Send the role's value rather than the picked option.
    if (filteredData.role) {
      filteredData.role = formValues.role.value;
    }

    onSubmit(filteredData);
  };

  return (
    <form id="edit-user-form" onSubmit={handleSubmit(submit)}>
      <div className={'input-container'}>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'email'}
              label={'Email'}
              placeholder={'email@email.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              onBlur={() => trigger('email')}
            />
          )}
        />
      </div>
      <UserNameFields control={control} trigger={trigger} />
      <div className={'input-container'}>
        <ProjectsMultiSelect
          label={'Project'}
          projects={sortedProjects}
          defaultValues={selectedProjects}
          onChange={(selected) => {
            setSelectedProjects(selected);
          }}
        />
      </div>
      <UserPasswordFields control={control} />
      <div className={'input-container'}>
        <Controller
          control={control}
          name="role"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'select'}
              label={'Role'}
              placeholder={'Admin'}
              invalidMessage={error?.message}
              value={value}
              selectOptions={roles}
              isValid={!error?.message}
              onChange={onChange}
              readonly={
                userInfo.role !== ROLES.COSSUPORT_MANAGER && user.role === ROLES.COSSUPORT_MANAGER
              }
            />
          )}
        />
      </div>
    </form>
  );
};
